import { PanelTab, BlockAction, Block } from '../types/panel'
import BlockRenderer from './BlockRenderer'

interface Props {
  tabs: PanelTab[]
  activeTabId: string | null
  onTabSelect: (id: string) => void
  onClose: () => void
  onAction?: (action: BlockAction) => void
}

/** Ref im Kontor-Stil aus der Tab-Id ableiten: tab-7 → A-2026-007 */
const refOf = (tabId: string): string => {
  const num = parseInt(tabId.split('-')[1] ?? '0', 10)
  return `A-${new Date().getFullYear()}-${String(num).padStart(3, '0')}`
}

const csvCell = (v: unknown): string => {
  const s = String(v ?? '')
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s
  return safe.includes(';') || safe.includes('\n') || safe.includes('"')
    ? `"${safe.replace(/"/g, '""')}"` : safe
}

const blocksToCsv = (blocks: Block[]): string => {
  return (Array.isArray(blocks) ? blocks : []).map(b => {
    if (b.type === 'table') {
      const columns = b.columns ?? []
      const header = columns.map(c => c.label).join(';')
      const rows = (b.rows ?? []).map(r =>
        columns.map(c => csvCell(r[c.key])).join(';')
      ).join('\n')
      return `${header}\n${rows}`
    }
    if (b.type === 'kpi') {
      const header = 'label;value;prefix;delta'
      const rows = (b.kpis ?? []).map(k =>
        `${k.label};${k.value};${k.prefix ?? ''};${k.delta ?? ''}`
      ).join('\n')
      return `${header}\n${rows}`
    }
    if (b.type === 'bar') {
      const header = 'label;value;max'
      const rows = (b.items ?? []).map(i => `${i.label};${i.value};${i.max}`).join('\n')
      return `${header}\n${rows}`
    }
    if (b.type === 'line') {
      const series = b.series ?? []
      const seriesHeaders = series.map(s => s.label).join(';')
      const header = `label;${seriesHeaders}`
      const rows = (b.labels ?? []).map((lbl, i) =>
        `${lbl};${series.map(s => (s.values ?? [])[i] ?? '').join(';')}`
      ).join('\n')
      return `${header}\n${rows}`
    }
    if (b.type === 'donut') {
      const header = 'label;value'
      const rows = (b.segments ?? []).map(s => `${s.label};${s.value}`).join('\n')
      return `${header}\n${rows}`
    }
    if (b.type === 'detail') {
      const header = 'feld;wert'
      const rows = (b.fields ?? []).map(f => `${csvCell(f.label)};${csvCell(f.value)}`).join('\n')
      return `${header}\n${rows}`
    }
    if (b.type === 'alert') {
      return `titel;text;tone\n${csvCell(b.title)};${csvCell(b.message)};${b.tone}`
    }
    return ''
  }).filter(Boolean).join('\n\n')
}

export default function Stage({ tabs, activeTabId, onTabSelect, onClose, onAction }: Props) {
  const activeTab = tabs.find(t => t.id === activeTabId) ?? tabs[tabs.length - 1]
  if (!activeTab) return <div className="k-stage"></div>

  const ref = refOf(activeTab.id)
  const num = String(parseInt(activeTab.id.split('-')[1] ?? '0', 10)).padStart(2, '0')
  const stand = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

  const exportCsv = () => {
    const csv = blocksToCsv(activeTab.payload.blocks)
    if (!csv) return
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${activeTab.payload.panelTitle}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="k-stage" data-screen-label={'Bühne: ' + ref}>
      <div className="k-stage__inner">
        <div className="k-stage__tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={'k-stage__tab' + (tab.id === activeTab.id ? ' is-active' : '')}
              onClick={() => onTabSelect(tab.id)}
              title={tab.payload.panelTitle}
            >
              {refOf(tab.id)}
            </button>
          ))}
          <button className="k-stage__close" onClick={onClose}>Schließen ×</button>
        </div>
        <div className="k-frame">
          <span className="k-corner k-corner--tl">{'// kontor / auswertung'}</span>
          <span className="k-corner k-corner--tr">ref {ref}</span>
          <span className="k-corner k-corner--bl">quelle: erp.live</span>
          <span className="k-corner k-corner--br">stand: heute {stand}</span>
          <div className="k-frame__head">
            <div className="k-frame__label">
              <span className="num">{num}</span>
              <span>/ {activeTab.payload.panelBadge ?? 'Auswertung'}</span>
            </div>
            <h2 className="k-frame__title">{activeTab.payload.panelTitle}</h2>
          </div>
          <div className="k-frame__body" key={activeTab.id}>
            <BlockRenderer blocks={activeTab.payload.blocks} onAction={onAction} />
          </div>
          <div className="k-frame__actions">
            <button className="k-btn k-btn--ghost" onClick={exportCsv}>
              Als CSV exportieren <span className="arr">→</span>
            </button>
            <span className="k-frame__hint">{(activeTab.payload.blocks ?? []).length} Block(e) · {ref}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
